if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Color-variant gallery: hide media that doesn't belong to the active color on load.
        this.initColorFilter();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      get sectionId() {
        return this.id.replace('MediaGallery-', '');
      }

      // Filters the gallery to the color selected in the variant picker on load.
      initColorFilter() {
        if (this.applyColorFilter()) return;
        // The variant picker may not be parsed yet on first element upgrade; retry once.
        if (this.dataset.colorOptionPosition) {
          document.addEventListener('DOMContentLoaded', () => this.applyColorFilter(), { once: true });
        }
      }

      // Reads the selected value of the product's Color option straight from the variant
      // radio (or the selected-variant JSON). The option's POSITION is provided by the
      // server via data-color-option-position, so no color names are hardcoded here.
      selectedColor() {
        const pos = parseInt(this.dataset.colorOptionPosition || '', 10);
        if (!pos) return '';
        const selects =
          document.querySelector(`variant-selects[data-section="${this.sectionId}"]`) ||
          document.getElementById(`variant-selects-${this.sectionId}`);
        if (!selects) return '';
        // 1. Radio / swatch picker: the checked input for this option position.
        const checked = selects.querySelector(`input[type="radio"][name$="-${pos}"]:checked`);
        if (checked && checked.value) return checked.value;
        // 2. Any picker type (e.g. dropdown): fall back to the selected-variant JSON.
        try {
          const variant = JSON.parse(selects.querySelector('[data-selected-variant]').textContent);
          if (variant?.options?.[pos - 1]) return variant.options[pos - 1];
        } catch (e) {}
        return '';
      }

      // Applies the color filter from the live selection; returns whether it could.
      applyColorFilter() {
        const color = this.selectedColor();
        if (color) {
          this.filterByColor(color);
          return true;
        }
        // Fallback (no color option position): use the active/first media's own tag.
        const active =
          this.elements.viewer.querySelector('li.is-active[data-media-color]') ||
          this.elements.viewer.querySelector('li[data-media-color]');
        if (active && active.dataset.mediaColor) {
          this.filterByColor(active.dataset.mediaColor);
          return true;
        }
        return false;
      }

      // Public: re-filter from the live radio selection (called on variant change).
      filterBySelectedColor() {
        const color = this.selectedColor();
        if (color) this.filterByColor(color);
      }

      // Shows only media tagged with `color` (plus untagged/universal media), hides the rest,
      // recomputes the slider counter, and keeps a visible image active. No-op when nothing is tagged.
      filterByColor(color) {
        if (!color) return;
        color = color.toString().trim().toLowerCase();
        if (!color) return;

        const matches = (el) => {
          const c = el.dataset.mediaColor;
          return !c || c === color;
        };

        const slides = this.elements.viewer.querySelectorAll('li[data-media-id]');
        slides.forEach((slide) => slide.classList.toggle('media-hidden--color', !matches(slide)));

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('li[data-target]')
            .forEach((thumb) => thumb.classList.toggle('media-hidden--color', !matches(thumb)));
        }

        // Keep the lightbox modal in sync (rendered as a sibling, matched by section id).
        const modal = document.getElementById(`ProductModal-${this.sectionId}`);
        if (modal) {
          modal
            .querySelectorAll('[data-media-color]')
            .forEach((item) => item.classList.toggle('media-hidden--color', !matches(item)));
        }

        // Recompute pages/counter now that some slides are hidden (initPages ignores 0-width items).
        if (typeof this.elements.viewer.resetPages === 'function') this.elements.viewer.resetPages();

        // If the active slide got hidden, promote the first visible one to be the main image.
        const currentActive = this.elements.viewer.querySelector('li[data-media-id].is-active');
        if (!currentActive || currentActive.classList.contains('media-hidden--color')) {
          const firstVisible = Array.from(slides).find(
            (slide) => !slide.classList.contains('media-hidden--color')
          );
          if (firstVisible) this.setActiveMedia(firstVisible.dataset.mediaId, false);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
